import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { getLogger } from '@packages/core/logging';
import { type RunDiagnosticResult, RunDiagnostic } from '@packages/domain/cvt';
import { GoogleAnalyticsEvents } from '@web/core/google-analytics';
import { useGoogleAnalytics } from '@web/core/google-analytics/use-google-analytics';
import { container } from '@web/services/container';
import { analyticsService } from '@web/services/singletons';
import { useCallback, useState } from 'react';

const logger = getLogger('cvt');

const [$runDiagnostic] = container.resolveOperations(RunDiagnostic);

/**
 * Runs a CVT diagnostic on an uploaded file. Holds whether an upload is running, so the
 * modal showing the upload can stay open until it ends.
 */
export function useCvtDiagnostic() {
    const { googleAnalyticsService } = useGoogleAnalytics();

    const [isUploading, setIsUploading] = useState<boolean>(false);

    const runDiagnostic = useCallback(
        async (file: File, vin: string | undefined, repairOrderNumber: string): Promise<RunDiagnosticResult> => {
            analyticsService.logEvent(ANALYTICS_EVENTS.CVT_FILE_UPLOAD_STARTED, {
                moduleName: 'cvt',
                fileName: file.name,
                fileSize: file.size,
                roNumber: repairOrderNumber,
            });

            if (!file || !vin || !repairOrderNumber) {
                throw new Error('File, VIN, and Repair Order Number are required to run diagnostic.');
            }

            setIsUploading(true);

            const startTime = Date.now();

            try {
                const result = await $runDiagnostic({ file, vin, repairOrderNumber });

                analyticsService.logEvent(ANALYTICS_EVENTS.CVT_UPLOAD_SUCCEEDED, {
                    moduleName: 'cvt',
                    fileName: file.name,
                    fileSize: file.size,
                    roNumber: repairOrderNumber,
                    result: 'success',
                    stsDiagnosticId: result.stsDiagnosticId,
                    latency: Date.now() - startTime,
                });
                googleAnalyticsService.track(GoogleAnalyticsEvents.UploadCvtDiagnostic, { success: true });

                return result;
            } catch (error) {
                const uploadError =
                    error instanceof Error ? error : new Error('An unexpected error occurred during upload.');

                logger.error('Failed to run the CVT diagnostic', { error, vin });
                analyticsService.logEvent(ANALYTICS_EVENTS.CVT_UPLOAD_FAILED, {
                    moduleName: 'cvt',
                    fileName: file.name,
                    fileSize: file.size,
                    roNumber: repairOrderNumber,
                    result: 'failed',
                    errorMessage: uploadError.message,
                    latency: Date.now() - startTime,
                });
                googleAnalyticsService.track(GoogleAnalyticsEvents.UploadCvtDiagnostic, { success: false });

                throw uploadError;
            } finally {
                setIsUploading(false);
            }
        },
        [googleAnalyticsService]
    );

    return {
        isUploading,
        runDiagnostic,
    };
}
